import React from "react";
import { FormControl, FormLabel, Input } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";

interface BaseFieldProps {
  name: string;
  label: string;
  required?: boolean;
  formControlSx?: SxProps;
}

export interface TextInputProps extends BaseFieldProps {
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: React.HTMLInputTypeAttribute;
  endDecorator?: React.ReactNode;
  readOnly?: boolean;
  disabled?: boolean;
  inputSx?: SxProps;
  inputProps?: any;
  formatCurrency?: boolean;
}

export const NumberInput: React.FC<TextInputProps> = ({
  name,
  label,
  value,
  onBlur,
  onChange,
  placeholder,
  type = "text",
  required,
  endDecorator,
  readOnly,
  disabled,
  formControlSx,
  inputSx,
  inputProps,
  formatCurrency,
}) => {
  const validateCurrencyInput = (inputValue: string): string => {
    // Remove any non-digit characters except decimal point
    let cleanValue = inputValue.replace(/[^0-9.]/g, "");

    // Handle multiple decimal points - keep only the first one
    const decimalParts = cleanValue.split(".");
    if (decimalParts.length > 2) {
      cleanValue = decimalParts[0] + "." + decimalParts.slice(1).join("");
    }

    // Split into integer and decimal parts
    const parts = cleanValue.split(".");
    let integerPart = parts[0] || "";
    let decimalPart = parts[1] || "";

    // Limit integer part to 11 digits
    if (integerPart.length > 11) {
      integerPart = integerPart.slice(0, 11);
    }

    // Limit decimal part to 2 digits
    if (decimalPart.length > 2) {
      decimalPart = decimalPart.slice(0, 2);
    }

    // Reconstruct the value
    if (cleanValue.includes(".")) {
      return integerPart + "." + decimalPart;
    } else {
      return integerPart;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (formatCurrency) {
      const inputValue = e.target.value;

      // Don't allow negative values
      if (inputValue.startsWith("-")) {
        return;
      }

      const validatedValue = validateCurrencyInput(inputValue);

      // Only update if the value has changed
      if (validatedValue !== inputValue) {
        e.target.value = validatedValue;
      }
    }

    onChange(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (formatCurrency) {
      // Prevent minus sign
      if (e.key === "-" || e.key === "Subtract") {
        e.preventDefault();
        return;
      }

      // Allow control keys
      const allowedKeys = ["Backspace", "Delete", "Tab", "Escape", "Enter", "Home", "End", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];

      if (allowedKeys.includes(e.key)) {
        return;
      }

      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      if (e.ctrlKey && ["a", "c", "v", "x", "z"].includes(e.key.toLowerCase())) {
        return;
      }

      // Allow numbers
      if (/^[0-9]$/.test(e.key)) {
        return;
      }

      // Allow decimal point only if there isn't one already
      if (e.key === "." && !e.currentTarget.value.includes(".")) {
        return;
      }

      // Block all other keys
      e.preventDefault();
    }

    // Call original onKeyDown if provided
    if (inputProps?.onKeyDown) {
      inputProps.onKeyDown(e);
    }
  };

  //   const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  //     if (formatCurrency) {
  //       e.preventDefault();
  //       const pastedText = e.clipboardData.getData("text");
  //       const validatedValue = validateCurrencyInput(pastedText);

  //       // Create a synthetic event for the validated value
  //       const syntheticEvent = {
  //         ...e,
  //         target: { ...e.target, value: validatedValue },
  //       } as React.ChangeEvent<HTMLInputElement>;

  //       onChange(syntheticEvent);
  //     }
  //   };

  return (
    <FormControl required={required} sx={formControlSx}>
      <FormLabel sx={{ fontWeight: "bold" }}>{label}</FormLabel>
      <Input
        name={name}
        value={value || ""}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder={placeholder}
        type={type}
        endDecorator={endDecorator}
        readOnly={readOnly}
        disabled={disabled}
        sx={inputSx}
        slotProps={{
          input: {
            ...inputProps,
            onKeyDown: handleKeyDown,
            // onPaste: handlePaste,
            inputMode: formatCurrency ? "decimal" : undefined,
            pattern: formatCurrency ? "[0-9]*\\.?[0-9]{0,2}" : undefined,
          },
        }}
      />
    </FormControl>
  );
};
